import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  Container,
  Card,
  CardContent,
  Tooltip,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Button,
  Backdrop,
  Snackbar,
  Alert,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import './monitorValutazioni_partner.css';
import { formGradimentoService } from '../../services/FormGradimento/FormGradimentoService';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RefreshIcon from '@mui/icons-material/Refresh';
import GetAppIcon from '@mui/icons-material/GetApp';
import * as XLSX from 'xlsx';

const MonitorValutazioni_Partner = () => {
  const [progetti, setProgetti] = useState([]);
  const [loadingFiltri, setLoadingFiltri] = useState(true);
  const [loadingValutazioni, setLoadingValutazioni] = useState(false);
  const [error, setError] = useState(null);
  const [expandedPanels, setExpandedPanels] = useState({});

  // Stati per la modalità selezione
  const [valutazioniSelezionate, setValutazioniSelezionate] = useState({});

  
  // Stato per le opzioni dei filtri
  const [opzioniFiltri, setOpzioniFiltri] = useState({
    listaProgetti: [],
    listaAnni: [],
    listaSottoProgetti: [],
    listaDestinazioni: []
  });

  // Stato per i valori selezionati nei filtri
  const [filtri, setFiltri] = useState({
    progetto: '',  // Valore vuoto per "Tutti"
    sottoprogetto: '', // Valore vuoto per "Tutti"
    nomeCognome: ''
  });

  // Funzione per caricare i dati dei filtri all'avvio
  useEffect(() => {
    const initFiltri = async () => {
      try {
        setLoadingFiltri(true);
        const response = await formGradimentoService.initFiltriValutazioni_partner();
        
        setOpzioniFiltri({
          listaProgetti: response.listaProgetti || [],
          listaAnni: response.listaAnni || [],
          listaSottoProgetti: response.listaSottoProgetti || [],
          listaDestinazioni: response.listaDestinazioni || []
        });
      } catch (err) {
        setError("Errore nel caricamento dei filtri: " + err.message);
      } finally {
        setLoadingFiltri(false);
      }
    };

    initFiltri();
  }, []);

  // Gestisce se una ricerca è stata effettuata o meno
  const [ricercaEffettuata, setRicercaEffettuata] = useState(false);

  // Funzione per cercare le valutazioni quando si preme il pulsante Cerca
  const cercaValutazioni = async () => {
    try {
      setLoadingValutazioni(true);
      setRicercaEffettuata(true);
      
      const response = await formGradimentoService.getValutazioniIntermedie_partner(filtri.progetto, filtri.sottoprogetto, filtri.nomeCognome.trim());
            
      setProgetti(response);

    } catch (err) {
      setError("Errore nel caricamento delle valutazioni: " + err.message);
    } finally {
      setLoadingValutazioni(false);
    }
  };

  const formattaData = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: it });
    } catch (e) {
      return '-';
    }
  };

  const formattaDataEdOra = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: it });
    } catch (e) {
      return '-';
    }
  };

  // Gestione espansione/compressione accordion
  const handleExpandAll = () => {
    const allExpanded = {};
    progetti.forEach(progetto => {
      allExpanded[progetto.idSottoProgetto] = true;
    });
    setExpandedPanels(allExpanded);
  };

  const handleCollapseAll = () => {
    setExpandedPanels({});
  };

  const handleAccordionChange = (progettoId) => (event, isExpanded) => {
    setExpandedPanels(prev => ({
      ...prev,
      [progettoId]: isExpanded
    }));
  };


  // Gestione selezione singola valutazione
  const handleSelectValutazione = (progettoId, idUtente, isSelected) => {
    setValutazioniSelezionate(prev => ({
      ...prev,
      [progettoId]: {
        ...prev[progettoId],
        [idUtente]: isSelected
      }
    }));
  };

  // Gestione selezione tutto tutto/niente niente per un progetto
  const handleSelectAll = (progettoId, selectAll) => {
    const progetto = progetti.find(p => p.idSottoProgetto === progettoId);
    if (!progetto || !progetto.listaValutazioni) return;

    const newSelections = {};
    progetto.listaValutazioni.forEach((valutazione) => {
      newSelections[valutazione.idUtente] = selectAll;
    });

    setValutazioniSelezionate(prev => ({
      ...prev,
      [progettoId]: newSelections
    }));
  };

  // Funzione per contare le selezioni di un progetto
  const getSelectedCount = (progettoId) => {
    const selections = valutazioniSelezionate[progettoId] || {};
    return Object.values(selections).filter(Boolean).length;
  };

  // Funzione per verificare se tutte le valutazioni sono selezionate
  const areAllSelected = (progettoId) => {
    const progetto = progetti.find(p => p.idSottoProgetto === progettoId);
    if (!progetto || !progetto.listaValutazioni || progetto.listaValutazioni.length === 0) return false;
    
    const selections = valutazioniSelezionate[progettoId] || {};
    return progetto.listaValutazioni.every((valutazione) => selections[valutazione.idUtente]);
  };


  // Definizione delle colonne della tabella
  const columns = [
    { 
      id: 'nominativo', 
      label: 'Nominativo', 
      format: (valutazione) => {
        const nome = valutazione.nome || '';
        const cognome = valutazione.cognome || '';
        return `${nome} ${cognome}`.trim() || '-';
      } 
    },
    { 
      id: 'idFeedback', 
      label: 'Stato valutazione', 
      minWidth: 200,
      format: (valutazione) => {
        if (!valutazione.haDatoFeedback) {
          // Se ha ricevuto il promemoria ma non ha ancora compilato
          if (valutazione.valutazioneIntermedia_primoAvviso) {
            const dataAvviso = formattaDataEdOra(valutazione.valutazioneIntermedia_primoAvviso);
            return (
              <Box>
                <div>Non ancora inviato</div>
                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                  Avvisato: {dataAvviso}
                </div>
              </Box>
            );
          }
          // Se non ha ricevuto il promemoria e non ha compilato
          return ( "Non ancora inviato" );
        }
        // Se ha compilato la valutazione
        return "ID: " + valutazione.idFeedback || '-';
      }
    },
    { id: 'dataCompilazione', label: 'Data Compilazione', format: formattaData },
    { id: 'valutazioneCoinvolgimento', label: '(1) Mi sento coinvolto/a', format: (value) => value || '-' },
    { id: 'valutazioneNuoveAbilita', label: '(2) Sto acquisendo nuove abilità', format: (value) => value || '-' },
    { id: 'valutazioneCaricoLavoro', label: '(3) Carico di Lavoro appropriato', format: (value) => value || '-' },
    { id: 'valutazioneIstruzioniChiare', label: '(4) Ricevo istruzione chiare', format: (value) => value || '-' },
    { id: 'valutazioneOrariLavoro', label: '(5) Orari di Lavoro sono rispettati', format: (value) => value || '-' },
    { id: 'valutazioneSistemazione', label: '(6) Aspettative sistemazione abitativa', format: (value) => value || '-' },
    { id: 'valutazionePosizioneSistemazione', label: '(7) Posizione alloggio comoda', format: (value) => value || '-' },
    { id: 'valutazioneTrasportiLocali', label: '(8) Trasporti locali adeguati', format: (value) => value || '-' },
    { id: 'valutazioneSupportoOrganizzazione', label: "(9) L'organizzazione mi sta supportando", format: (value) => value || '-' },
    { id: 'commentiPositivi', label: 'Commenti Positivi', format: (value) => value || '-' },
    { id: 'commentiNegativi', label: 'Commenti Negativi', format: (value) => value || '-' },
    { id: 'altriCommenti', label: 'Altri Commenti', format: (value) => value || '-' },
  ];

  // Gestione dei filtri
  const handleFiltroChange = (event) => {
    const { name, value } = event.target;
    setFiltri(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loadingFiltri) {
    return (
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={true}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Caricamento filtri...
          </Typography>
        </Box>
      </Backdrop>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error" variant="h6">
          Errore: {error}
        </Typography>
      </Box>
    );
  }

  // Funzione per generare e scaricare il file Excel complessivo
  const handleExcelDownload = () => {
    try {
      // Array per contenere tutti i fogli di lavoro
      const workbooks = [];
      
      // Per ogni progetto, crea un foglio Excel
      progetti.forEach(progetto => {
        if (!progetto.listaValutazioni || progetto.listaValutazioni.length === 0) {
          return; // Salta i progetti senza valutazioni
        }
        
        // Crea intestazioni dalle colonne
        const headers = columns.map(col => col.label);
        
        // Prepara i dati per il foglio Excel
        const data = [
          headers, // Prima riga con le intestazioni
          ...progetto.listaValutazioni.map(valutazione => {
            return columns.map(col => {
              // Gestisci formattazione speciale per alcune colonne
              if (col.id === 'nominativo') {
                const nome = valutazione.nome || '';
                const cognome = valutazione.cognome || '';
                return `${nome} ${cognome}`.trim() || '-';
              } else if (col.id === 'idFeedback') {
                return valutazione.haDatoFeedback ? 
                  valutazione.idFeedback || '-' : 
                  'Non ancora inviato';
              } else if (col.id === 'dataCompilazione') {
                return formattaData(valutazione[col.id]);
              } else {
                // Per le altre colonne, viene usato direttamente il suo valore o '-' se null
                return valutazione[col.id] || '-';
              }
            });
          })
        ];
        
        // Crea un foglio di lavoro per questo progetto
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        
        // Aggiungi all'array di fogli
        workbooks.push({
          name: `${progetto.nomeProgetto} ${progetto.cittaDestinazione} ${progetto.inizioMobilita} - ${progetto.fineMobilita}`, // [${progetto.codiceProgetto}]
          data: worksheet
        });
      });
      
      // Nel caso in cui, per qualche motivo, non ci fossero dati da esportare
      if (workbooks.length === 0) {
        //setSnackbarMessage('Nessun dato disponibile per l\'esportazione');
        //setSnackbarSeverity('warning');
        //setSnackbarOpen(true);
        return;
      }
      
      // Creo un nuovo libro Excel
      const wb = XLSX.utils.book_new();

      // Tengo traccia dei nomi dei fogli già aggiunti per evitare duplicati
      const usedSheetNames = new Set();
      
      // Aggiungi tutti i fogli di lavoro al libro
      workbooks.forEach((workbook, index) => {

        // Sostituisco i caratteri non validi nel nome del foglio, Excel non permette : \ / ? * [ ]
        let sheetName = workbook.name.replace(/[:\\\/\?\*\[\]]/g, '_');

        // Limita la lunghezza del nome del foglio a 31 caratteri (limite Excel)
        if (sheetName.length > 31) {
          sheetName = sheetName.substring(0, 28) + '...';
        }
        
        // Se un foglio con questo nome esiste già, aggiungo un numero
        let uniqueSheetName = sheetName;
        let counter = 1;

        while (usedSheetNames.has(uniqueSheetName)) {
          const suffix = ` (${counter})`;
          // Mi assicuro che il nome con il suffisso non superi 31 caratteri
          uniqueSheetName = sheetName.length + suffix.length > 31 ?
            sheetName.substring(0, 31 - suffix.length) + suffix :
            sheetName + suffix;
          counter++;
        }

        // Aggiungo il nome del foglio all'insieme dei nomi usati
        usedSheetNames.add(uniqueSheetName);
        
        XLSX.utils.book_append_sheet(wb, workbook.data, sheetName);
      });
      
      // Genera il nome del file con la data corrente
      const currentDate = format(new Date(), 'yyyy-MM-dd', { locale: it });
      const fileName = `Estrazione complessiva Valutazioni intermedie (${currentDate}).xlsx`;
      
      // Genera il file e avvia il download
      XLSX.writeFile(wb, fileName);
      
      // Mostra un messaggio di successo
      //setSnackbarMessage('File Excel generato con successo');
      //setSnackbarSeverity('success');
      //setSnackbarOpen(true);
    } catch (error) {
      console.error('Errore durante la generazione del file Excel:', error);
      //setSnackbarMessage('Errore durante la generazione del file Excel');
      //setSnackbarSeverity('error');
      //setSnackbarOpen(true);
    }
  };

  // Funzione unificata per il download Excel
  const handleExcelDownloadProgetto = (progetto, soloSelezionati = false) => {
    try {
      let valutazioniDaEsportare = progetto.listaValutazioni || [];

      // Se richiesto solo le selezionate, filtra
      if (soloSelezionati) {
        const selections = valutazioniSelezionate[progetto.idSottoProgetto] || {};
        valutazioniDaEsportare = progetto.listaValutazioni.filter(
          valutazione => selections[valutazione.idUtente]
        );
      }

      if (valutazioniDaEsportare.length === 0) {
        console.warn(soloSelezionati ? 'Nessuna valutazione selezionata per il download' : 'Nessuna valutazione disponibile per questo progetto');
        return;
      }

      // Crea intestazioni dalle colonne
      const headers = columns.map(col => col.label);

      // Prepara i dati per il foglio Excel
      const data = [
        headers, // Prima riga con le intestazioni
        ...valutazioniDaEsportare.map(valutazione => {
          return columns.map(col => {
            // Gestisci formattazione speciale per alcune colonne
            if (col.id === 'nominativo') {
              const nome = valutazione.nome || '';
              const cognome = valutazione.cognome || '';
              return `${nome} ${cognome}`.trim() || '-';
            } else if (col.id === 'idFeedback') {
              return valutazione.haDatoFeedback ?
                valutazione.idFeedback || '-' :
                'Non ancora inviato';
            } else if (col.id === 'dataCompilazione') {
              return formattaData(valutazione[col.id]);
            } else {
              return valutazione[col.id] || '-';
            }
          });
        })
      ];

      // Crea un foglio di lavoro
      const worksheet = XLSX.utils.aoa_to_sheet(data);

      // Crea un nuovo libro Excel
      const wb = XLSX.utils.book_new();

      // Nome del foglio
      let sheetName = soloSelezionati ?
        `${progetto.nomeProgetto} (selezionate)` :
        `${progetto.nomeProgetto} ${progetto.cittaDestinazione}`;
      sheetName = sheetName.replace(/[:\\\/\?\*\[\]]/g, '_');
      if (sheetName.length > 31) {
        sheetName = sheetName.substring(0, 28) + '...';
      }

      // Aggiungi il foglio al libro
      XLSX.utils.book_append_sheet(wb, worksheet, sheetName);

      // Genera il nome del file
      const currentDate = format(new Date(), 'yyyy-MM-dd', { locale: it });
      const fileName = soloSelezionati ?
        `Valutazioni selezionate ${progetto.nomeProgetto} [${progetto.codiceProgetto}] (${valutazioniDaEsportare.length} utenti) (${currentDate}).xlsx` :
        `Valutazioni ${progetto.nomeProgetto} [${progetto.codiceProgetto}] (${currentDate}).xlsx`;

      // Genera il file e avvia il download
      XLSX.writeFile(wb, fileName);

    } catch (error) {
      console.error('Errore durante la generazione del file Excel:', error);
    }
  };


  return (
    <Container maxWidth="xxl" className="feedback-container">
      <Typography variant="h3" component="h1" gutterBottom className="titolo-pagina">
        Monitor valutazioni Intermedie
      </Typography>

      {/* Filtri */}
      <Accordion className="filtri-container" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            <SearchIcon fontSize="small" sx={{ mr: 1 }} />
            <strong>Filtri di ricerca</strong>
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel id="filtro-progetto-label">Progetto</InputLabel>
                <Select
                  labelId="filtro-progetto-label"
                  id="filtro-progetto"
                  name="progetto"
                  value={filtri.progetto}
                  onChange={handleFiltroChange}
                  label="Progetto"
                >
                  <MenuItem value="">Tutti</MenuItem>
                  {opzioniFiltri.listaProgetti.map(p => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel id="filtro-sottoprogetto-label">Sottoprogetto</InputLabel>
                <Select
                  labelId="filtro-sottoprogetto-label"
                  id="filtro-sottoprogetto"
                  name="sottoprogetto"
                  value={filtri.sottoprogetto}
                  onChange={handleFiltroChange}
                  label="Sottoprogetto"
                >
                  <MenuItem value="">Tutti</MenuItem>
                  {opzioniFiltri.listaSottoProgetti && opzioniFiltri.listaSottoProgetti.map(p => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <TextField
                fullWidth
                id="filtro-nome-cognome"
                name="nomeCognome"
                label="Cerca per Nome/Cognome"
                variant="outlined"
                size="small"
                value={filtri.nomeCognome}
                onChange={handleFiltroChange}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
        <AccordionActions>
          <Button
            variant="contained"
            color="primary"
            onClick={cercaValutazioni}
            disabled={loadingValutazioni}
          >
            {loadingValutazioni ? 'Cerca' : 'Cerca'}
          </Button>
        </AccordionActions>
      </Accordion>

      <Box sx={{ marginTop: '33px' }}>
        <Typography variant="h6">
          Lista Progetti e Valutazioni
        </Typography>
      </Box>
      

      {/* Pulsanti globali */}
      {!loadingValutazioni && progetti.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1 }}>
          <Tooltip
            title="Espandi tutte le sezioni visualizzate"
            arrow
            placement="top"
          >
            <Button
              variant="outlined"
              startIcon={<UnfoldMoreIcon />}
              onClick={handleExpandAll}
              size="small"
            >
              Espandi tutto
            </Button>
          </Tooltip>

          <Tooltip
            title="Comprimi tutte le sezioni visualizzate"
            arrow
            placement="top"
          >
            <Button
              variant="outlined"
              startIcon={<UnfoldLessIcon />}
              onClick={handleCollapseAll}
              size="small"
            >
              Comprimi tutto
            </Button>
          </Tooltip>

          <Tooltip
            title="Scarica un estrazione Excel di tutte le valutazioni di tutti i progetti visualizzati in questa pagina. Ogni progetto verrà inserito in un foglio di Excel differente"
            arrow
            placement="top"
          >
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              size="small"
              onClick={handleExcelDownload}
            >
              Scarica tutte le valutazioni
            </Button>
          </Tooltip>
        </Box>
      )}

      {/* Indicatore di caricamento per le valutazioni */}
      {loadingValutazioni && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loadingValutazioni && progetti.length > 0 && progetti.map((progetto) => {
        
        // Verifico se ci sono utenti e valutazioni per questo progetto
        const hasValutazioni = progetto.listaValutazioni && progetto.listaValutazioni.length > 0;

        const selectedCount = getSelectedCount(progetto.idSottoProgetto);
        const allSelected = areAllSelected(progetto.idSottoProgetto);
        
        return (
          <Accordion
            key={progetto.idSottoProgetto}
            className="progetto-accordion"
            expanded={expandedPanels[progetto.idSottoProgetto] === true}
            onChange={handleAccordionChange(progetto.idSottoProgetto)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel-${progetto.idSottoProgetto}-content`}
              id={`panel-${progetto.idSottoProgetto}-header`}
              className="accordion-header"
            >
              <Typography variant="h6" sx={{ width: '40%', flexShrink: 0 }}>
                Progetto '{progetto.nomeProgetto}' [{progetto.codiceProgetto}]
              </Typography>
              
              <Typography variant="h6">
                {progetto.cittaDestinazione}, dal {formattaData(progetto.inizioMobilita)} al {formattaData(progetto.fineMobilita)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>

              {/* Toolbar delle azioni per ogni progetto */}
              {hasValutazioni && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>

                  {selectedCount <= 0 ? (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <Tooltip
                          title="Scarica un estrazione Excel di tutte le valutazioni del singolo progetto"
                          arrow
                          placement="top"
                        >
                          <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            size="small"
                            onClick={() => handleExcelDownloadProgetto(progetto, false)}
                          >
                            Scarica valutazioni del progetto
                          </Button>
                        </Tooltip>

                      </Box>
                    </Box>

                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>

                        <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium', marginTop: '7px' }}>
                          {selectedCount} selezionati
                        </Typography>

                        <Tooltip
                          title="Scarica un estrazione Excel delle valutazioni solo degli utenti selezionati"
                          arrow
                          placement="top"
                        >
                          <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            size="small"
                            onClick={() => handleExcelDownloadProgetto(progetto, true)}
                          >
                            Scarica valutazioni selezionate
                          </Button>
                        </Tooltip>

                      </Box>
                    </Box>
                  )}

                </Box>
              )}

              <Paper className='feedback-card'>
                  {hasValutazioni ? (
                    <Paper>
                      <TableContainer className="feedback-table-container">
                        <Table stickyHeader aria-label={`tabella feedback ${progetto.nomeProgetto}`} className="feedback-table">
                          <TableHead>
                            <TableRow>
                            {/* Colonna checkbox */}
                            <TableCell padding="checkbox" className="table-header-cell">
                              <Checkbox
                                indeterminate={selectedCount > 0 && !allSelected}
                                checked={allSelected}
                                onChange={() => handleSelectAll(progetto.idSottoProgetto, !allSelected)}
                              />
                            </TableCell>
                              {columns.map((column) => (
                                <TableCell
                                  key={column.id}
                                  align="left"
                                  className="table-header-cell"
                                  style={{ minWidth: column.minWidth }}
                                >
                                  {column.label}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {progetto.listaValutazioni.map((valutazione) => {
                                const isSelected = valutazioniSelezionate[progetto.idSottoProgetto]?.[valutazione.idUtente] || false;

                                return (
                                  <TableRow
                                    hover
                                    role="checkbox"
                                    tabIndex={-1}
                                    key={valutazione.idUtente}
                                    className={`${!valutazione.haDatoFeedback ? 'row-feedback-missing' : ''} ${isSelected ? 'row-selected' : ''}`}
                                    selected={isSelected}
                                  >
                                    {/* Checkbox per selezione */}
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        checked={isSelected}
                                        onChange={(event) => handleSelectValutazione(progetto.idSottoProgetto, valutazione.idUtente, event.target.checked)}
                                      />
                                    </TableCell>
                                    {columns.map((column) => {

                                      return (
                                        <TableCell key={column.id} align="left">
                                          {column.id === 'nominativo' ? (
                                            // Per la colonna nominativo
                                            <div className="cell-content">
                                              {column.format(valutazione)}
                                            </div>
                                          ) : column.id === 'idFeedback' ? (
                                            // Per la colonna idFeedback (contiene i chip)
                                            column.format(valutazione)
                                          ) : column.label.includes('Commenti') && valutazione.haDatoFeedback ? (
                                            // Solo per le colonne dei commenti visualizzo il tooltip
                                            <Tooltip title={column.format(valutazione[column.id])} arrow placement="top">
                                              <div className="cell-content cell-commenti" data-id={column.id}>
                                                {column.format(valutazione[column.id])}
                                              </div>
                                            </Tooltip>
                                          ) : (
                                            // Per tutte le altre colonne nessun tooltip
                                            <div className="cell-content" data-id={column.id}>
                                              {column.format(valutazione[column.id])}
                                            </div>
                                          )}
                                        </TableCell>
                                      );
                                    })}
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  ) : (
                    <Typography variant="body1" sx={{ padding: 2 }}>
                      Nessun utente assegnato per questo progetto.
                    </Typography>
                  )}
                  </Paper>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {!loadingValutazioni && progetti.length === 0 && (
        <Typography variant="body1" sx={{ padding: 2 }}>
          {ricercaEffettuata ? 
            'Nessun risultato trovato per i filtri selezionati.' : 
            'Effettua una ricerca per visualizzare le valutazioni.'}
        </Typography>
      )}


    </Container>
  );
};


export default MonitorValutazioni_Partner;